import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  FormControl,
  Chip,
  Snackbar,
  Alert,
} from '@mui/material';
import { Search, Plus, Filter } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Task, TaskStatus, TaskPriority } from '@/types';
import { taskService } from '@/services/api';
import { QUERY_KEYS } from '@/services/queryClient';
import { useAppDispatch, useAppSelector } from '@/store';
import { selectCurrentUser } from '@/store/slices/authSlice';
import { openModal } from '@/store/slices/uiSlice';
import ConfirmationModal from '@/shared/components/modals/ConfirmationModal';
import { useSnackbar } from '@/shared/contexts/SnackbarContext';
import {
  TaskTable,
  useTaskTableActions,
  PROJECT_TASKS_CONFIG,
  canDelete,
  canEdit,
  BulkTaskActionsBar,
} from '@/shared/components/TaskTable';
import { getDeleteTaskMessage } from '@/shared/components/TaskTable/utils';
import { AppSelect, AppMenuItem } from '@/shared/components/AppSelect';

interface ProjectTasksTabProps {
  projectId: string;
  tasks: Task[];
  isLoading: boolean;
  onCreateTask: () => void;
  onTaskClick?: (taskId: string) => void;
  canCreateTask?: boolean;
}

/**
 * ProjectTasksTab
 * Project task list using shared TaskTable with filtering toolbar
 * Supports multi-select + bulk delete
 */
const ProjectTasksTab: React.FC<ProjectTasksTabProps> = ({
  projectId,
  tasks,
  isLoading,
  onCreateTask,
  onTaskClick,
  canCreateTask = true,
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const user = useAppSelector(selectCurrentUser);
  const { showError, showSuccess } = useSnackbar();

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<TaskStatus | 'all'>('all');
  const [priorityFilter, setPriorityFilter] = useState<TaskPriority | 'all'>('all');
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuTask, setMenuTask] = useState<Task | null>(null);
  const [selectedTaskIds, setSelectedTaskIds] = useState<Set<string>>(new Set());
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);

  const filteredTasks = useMemo(() => {
    return tasks.filter((task) => {
      const matchesSearch =
        !searchQuery || task.title.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'all' || task.status === statusFilter;
      const matchesPriority = priorityFilter === 'all' || task.priority === priorityFilter;
      return matchesSearch && matchesStatus && matchesPriority;
    });
  }, [tasks, searchQuery, statusFilter, priorityFilter]);

  const {
    handleCheckboxChange,
    handleComplete,
    confirmDelete,
    confirmCompleteTask,
    confirmDeleteTask,
    blockingWarning,
    blockingTask,
    setConfirmCompleteTask,
    setConfirmDeleteTask,
    clearBlockingWarning,
  } = useTaskTableActions({
    context: 'projectTasks',
    projectId,
    allTasks: tasks,
  });

  useEffect(() => {
    const visibleIds = new Set(filteredTasks.map((t) => t.id));
    setSelectedTaskIds((prev) => {
      const next = new Set(Array.from(prev).filter((id) => visibleIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [filteredTasks]);

  const selectedTasks = useMemo(
    () => filteredTasks.filter((t) => selectedTaskIds.has(t.id)),
    [filteredTasks, selectedTaskIds]
  );

  const bulkDeleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const results = await Promise.allSettled(ids.map((id) => taskService.deleteTask(id)));
      const failed = results.filter((r) => r.status === 'rejected').length;
      const succeeded = results.length - failed;
      return { succeeded, failed };
    },
    onSuccess: ({ succeeded, failed }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tasks });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.myTasks });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.projectTasks(projectId) });
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEYS.projects, projectId] });
      setSelectedTaskIds(new Set());
      setConfirmBulkDelete(false);
      if (failed > 0) {
        showError(`Deleted ${succeeded} task(s); ${failed} failed`);
      } else {
        showSuccess(`Deleted ${succeeded} task${succeeded === 1 ? '' : 's'}`);
      }
    },
    onError: (error) => {
      showError(error instanceof Error ? error.message : 'Failed to delete tasks');
      setConfirmBulkDelete(false);
    },
  });

  const handleTaskClick = (taskId: string) => {
    if (onTaskClick) {
      onTaskClick(taskId);
    } else {
      navigate(`/tasks?taskId=${taskId}`);
    }
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuTask(null);
  };

  const handleSelectionChange = (taskId: string, selected: boolean) => {
    setSelectedTaskIds((prev) => {
      const next = new Set(prev);
      if (selected) next.add(taskId);
      else next.delete(taskId);
      return next;
    });
  };

  const handleSelectAllChange = (selected: boolean) => {
    if (!selected) {
      setSelectedTaskIds(new Set());
      return;
    }
    const deletableIds = filteredTasks
      .filter((task) => user && canDelete(task, user))
      .map((task) => task.id);
    setSelectedTaskIds(new Set(deletableIds));
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search tasks..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={16} />
                </InputAdornment>
              ),
            }}
            sx={{ width: 250 }}
          />

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <AppSelect
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as TaskStatus | 'all')}
              displayEmpty
              fullWidth={false}
            >
              <AppMenuItem value="all">All Status</AppMenuItem>
              <AppMenuItem value={TaskStatus.NOT_STARTED}>Not Started</AppMenuItem>
              <AppMenuItem value={TaskStatus.IN_PROGRESS}>In Progress</AppMenuItem>
              <AppMenuItem value={TaskStatus.BLOCKED}>Blocked</AppMenuItem>
              <AppMenuItem value={TaskStatus.COMPLETED}>Completed</AppMenuItem>
              <AppMenuItem value={TaskStatus.CANCELLED}>Cancelled</AppMenuItem>
            </AppSelect>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <AppSelect
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value as TaskPriority | 'all')}
              displayEmpty
              fullWidth={false}
            >
              <AppMenuItem value="all">All Priority</AppMenuItem>
              <AppMenuItem value="p1">P1 - Critical</AppMenuItem>
              <AppMenuItem value="p2">P2 - High</AppMenuItem>
              <AppMenuItem value="p3">P3 - Medium</AppMenuItem>
              <AppMenuItem value="p4">P4 - Low</AppMenuItem>
            </AppSelect>
          </FormControl>
        </Box>

        {canCreateTask && (
          <Button variant="contained" startIcon={<Plus size={16} />} onClick={onCreateTask}>
            Add Task
          </Button>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Chip
          icon={<Filter size={14} />}
          label={`${filteredTasks.length} tasks`}
          size="small"
          variant="outlined"
        />
        <Chip
          label={`${filteredTasks.filter((t) => t.status === TaskStatus.COMPLETED).length} completed`}
          size="small"
          color="success"
          variant="outlined"
        />
        <Chip
          label={`${filteredTasks.filter((t) => (t.blockedByTaskIds?.length || 0) > 0).length} blocked`}
          size="small"
          color="warning"
          variant="outlined"
        />
      </Box>

      <TaskTable
        tasks={filteredTasks}
        isLoading={isLoading}
        context="projectTasks"
        config={PROJECT_TASKS_CONFIG}
        selectedTaskIds={selectedTaskIds}
        emptyStateMessage={
          tasks.length === 0 ? 'No tasks in this project yet' : 'No tasks match your filters'
        }
        emptyStateAction={
          tasks.length === 0 && canCreateTask ? (
            <Button startIcon={<Plus size={16} />} onClick={onCreateTask} sx={{ mt: 1 }}>
              Create First Task
            </Button>
          ) : undefined
        }
        onTaskClick={handleTaskClick}
        onCheckboxChange={handleCheckboxChange}
        onSelectionChange={handleSelectionChange}
        onSelectAllChange={handleSelectAllChange}
        onMenuAction={(action, task, event) => {
          if (action === 'menu' && event) {
            setMenuAnchor(event.currentTarget);
            setMenuTask(task);
          }
        }}
      />

      <BulkTaskActionsBar
        selectedCount={selectedTaskIds.size}
        isDeleting={bulkDeleteMutation.isPending}
        onClear={() => setSelectedTaskIds(new Set())}
        onDelete={() => setConfirmBulkDelete(true)}
      />

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { minWidth: 160 } }}
      >
        <MenuItem
          onClick={() => {
            if (menuTask) handleTaskClick(menuTask.id);
            handleMenuClose();
          }}
        >
          View details
        </MenuItem>
        {menuTask &&
          user &&
          canEdit(menuTask, user) &&
          menuTask.status !== TaskStatus.COMPLETED &&
          menuTask.status !== TaskStatus.CANCELLED && (
            <MenuItem
              onClick={() => {
                dispatch(
                  openModal({
                    type: 'taskEdit',
                    data: menuTask as unknown as Record<string, unknown>,
                  })
                );
                handleMenuClose();
              }}
            >
              Edit task
            </MenuItem>
          )}
        {menuTask && user && canDelete(menuTask, user) && (
          <MenuItem
            onClick={() => {
              setConfirmDeleteTask(menuTask);
              handleMenuClose();
            }}
            sx={{ color: 'error.main' }}
          >
            Delete
          </MenuItem>
        )}
      </Menu>

      {confirmCompleteTask && (
        <ConfirmationModal
          open={true}
          onClose={() => setConfirmCompleteTask(null)}
          data={{
            title: 'Mark as completed',
            message: `Are you sure you want to mark "${confirmCompleteTask.title}" as completed?`,
            type: 'success',
            confirmText: 'Complete',
            onConfirm: () => handleComplete(confirmCompleteTask),
          }}
        />
      )}

      {confirmDeleteTask && (
        <ConfirmationModal
          open={true}
          onClose={() => setConfirmDeleteTask(null)}
          data={{
            title: 'Delete task',
            message: getDeleteTaskMessage(confirmDeleteTask),
            type: 'danger',
            confirmText: 'Delete',
            onConfirm: () => confirmDelete(confirmDeleteTask),
          }}
        />
      )}

      {confirmBulkDelete && (
        <ConfirmationModal
          open={true}
          onClose={() => setConfirmBulkDelete(false)}
          data={{
            title: 'Delete selected tasks',
            message: `Delete ${selectedTasks.length} selected task${
              selectedTasks.length === 1 ? '' : 's'
            }? This cannot be undone.`,
            type: 'danger',
            confirmText: `Delete ${selectedTasks.length}`,
            onConfirm: () =>
              bulkDeleteMutation.mutate(selectedTasks.map((task) => task.id)),
          }}
        />
      )}

      <Snackbar
        open={!!blockingWarning}
        autoHideDuration={6000}
        onClose={clearBlockingWarning}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={clearBlockingWarning}
          severity="warning"
          action={
            blockingTask ? (
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  handleTaskClick(blockingTask.id);
                  clearBlockingWarning();
                }}
              >
                View Blocker
              </Button>
            ) : undefined
          }
        >
          {blockingWarning}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProjectTasksTab;
